import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  getWorkflowDisplayLabel,
  getWorkflowDisplayTooltip,
  normalizeWorkflowDisplaySettings,
} from "../utils/workflowDisplayResolver";

const CACHE_TTL_MS = 5 * 60 * 1000;
const settingsCache = new Map();

const getCacheKey = (instanceUniqueId) => String(instanceUniqueId || "").trim();

const readCache = (cacheKey) => {
  if (!cacheKey) return null;
  const cached = settingsCache.get(cacheKey);
  if (!cached) return null;
  if (Date.now() - cached.ts > CACHE_TTL_MS) {
    settingsCache.delete(cacheKey);
    return null;
  }
  return cached.settings;
};

const writeCache = (cacheKey, settings) => {
  if (!cacheKey) return;
  settingsCache.set(cacheKey, { ts: Date.now(), settings });
};

export default function useWorkflowDisplaySettings(instanceUniqueId) {
  const [workflowDisplaySettings, setWorkflowDisplaySettings] = useState(
    normalizeWorkflowDisplaySettings({})
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = useMemo(
    () => ({ headers: { accessToken: localStorage.getItem("accessToken") } }),
    []
  );

  const load = useCallback(
    async ({ force = false } = {}) => {
      const cacheKey = getCacheKey(instanceUniqueId);
      if (!cacheKey) {
        setWorkflowDisplaySettings(normalizeWorkflowDisplaySettings({}));
        return;
      }

      if (!force) {
        const cached = readCache(cacheKey);
        if (cached) {
          setWorkflowDisplaySettings(cached);
          return;
        }
      }

      setLoading(true);
      setError(null);
      try {
        const instanceRes = await axios.get(
          `${SERVER}/instances/byinstanceUniqueId/${instanceUniqueId}`,
          headers
        );
        const instanceId = instanceRes.data?.id;
        if (!instanceId) {
          throw new Error("Unable to resolve instance id.");
        }

        const defaultsRes = await axios.get(
          `${SERVER}/instances/${instanceId}/defaults`,
          headers
        );

        const normalized = normalizeWorkflowDisplaySettings(
          defaultsRes.data?.defaults?.workflowDisplaySettings || {}
        );

        writeCache(cacheKey, normalized);
        setWorkflowDisplaySettings(normalized);
      } catch (err) {
        setError(err);
        setWorkflowDisplaySettings(normalizeWorkflowDisplaySettings({}));
      } finally {
        setLoading(false);
      }
    },
    [SERVER, headers, instanceUniqueId]
  );

  useEffect(() => {
    load();
  }, [load]);

  const resolveLabel = useCallback(
    (group, statusKey) =>
      getWorkflowDisplayLabel({
        workflowDisplaySettings,
        group,
        statusKey,
      }),
    [workflowDisplaySettings]
  );

  const resolveTooltip = useCallback(
    (group, statusKey) =>
      getWorkflowDisplayTooltip({
        workflowDisplaySettings,
        group,
        statusKey,
      }),
    [workflowDisplaySettings]
  );

  const refresh = useCallback(() => load({ force: true }), [load]);

  return {
    workflowDisplaySettings,
    loading,
    error,
    getWorkflowLabel: resolveLabel,
    getWorkflowTooltip: resolveTooltip,
    refresh,
  };
}

export const clearWorkflowDisplaySettingsCache = () => {
  settingsCache.clear();
};
